<template>
  <b-tabs
    content-class="mt-3"
    small
    fill
  >
    <b-tab title="Images">
      <b-btn-group vertical>
        <b-btn
          variant="outline-primary"
          @click="addProductImages"
        >
          <b-icon icon="file-earmark-image" />
          Add product images...
        </b-btn>
        <b-btn
          variant="outline-primary"
          @click="addProductImagePlaceholder"
        >
          <b-icon icon="file-earmark-image" />
          Add product image placeholder...
        </b-btn>
        <b-btn
          variant="outline-primary"
          @click="addProductDimensions"
        >
          <b-icon icon="aspect-ratio" />
          {{ hasProductDimensionsField ? 'Edit' : 'Add' }} product dimensions...
        </b-btn>
        <b-btn
          variant="outline-primary"
          @click="addImage"
        >
          <b-icon icon="card-image" />
          Add image...
        </b-btn>
        <b-btn
          variant="outline-primary"
          @click="addAdditionalField('Background Theme Image')"
        >
          <b-icon icon="card-image" />
          Add image placeholder...
        </b-btn>
        <b-btn
          variant="outline-primary"
          @click="showSmartObject"
        >
          <b-icon icon="card-image" />
          Add Smart Object...
        </b-btn>
      </b-btn-group>
    </b-tab>
    <b-tab title="Add Fields">
      <b-btn-group vertical>
        <b-btn
          variant="outline-primary"
          @click="addAdditionalField('Multi Line Text')"
        >
          <b-icon icon="input-cursor-text" />
          Multi Line Text
        </b-btn>
        <b-btn
          variant="outline-primary"
          @click="addAdditionalField('Rectangle')"
        >
          <b-icon icon="square" />
          Rectangle
        </b-btn>
        <b-btn
          variant="outline-primary"
          @click="addAdditionalField('Circle')"
        >
          <b-icon icon="circle" />
          Circle
        </b-btn>
        <b-btn
          variant="outline-primary"
          @click="addAdditionalField('Safe Zone')"
        >
          <b-icon icon="x-square" />
          Safe Zone
        </b-btn>
        <b-btn
          variant="outline-primary"
          @click="addAdditionalField('Icons')"
        >
          <b-icon icon="file-earmark-image" />
          Icons
        </b-btn>
        <b-btn
          variant="outline-primary"
          @click="addAdditionalField('Line')"
        >
          <b-icon icon="border-width" />
          Line
        </b-btn>
        <b-btn
          variant="outline-primary"
          @click="addAdditionalField('Table')"
        >
          <b-icon icon="table" />
          Table
        </b-btn>
        <b-btn
          variant="outline-primary"
          @click="addAdditionalField('Cut Line')"
        >
          <b-icon icon="scissors" />
          Cut Line
        </b-btn>
      </b-btn-group>

      <IconsSettingsModal ref="iconsSettingsModal" />
      <SimpleTableBuilderModal />
    </b-tab>
    <div class="mt-2">
      <b-input
        v-model="fieldName"
        type="text"
        placeholder="Field name"
        class="form-control mb-2"
      />
      <div>
        <span>Add to</span>
        <PreviewsDropdown v-model="selectedInstancesIds" />
      </div>
    </div>
  </b-tabs>
</template>

<script>
import { mapGetters } from 'vuex'
import { editorControlEventBroker, EVENTS } from '@frontend/group/modules/editor-control-popup/event-broker';
import {
  FIELD_TYPE_BACKGROND_THEME_IMAGE,
  FIELD_TYPE_CIRCLE, FIELD_TYPE_CUT_LINE, FIELD_TYPE_LINE,
  FIELD_TYPE_MULTI_LINE_TEXT,
  FIELD_TYPE_RECTANGLE,
  FIELD_TYPE_SAFE_ZONE
} from "@frontend/constants/type-fields-of-template";
import PreviewsDropdown from "@frontend/components/modules/layouts-page/EditorControl/components/PreviewsDropdown.vue";
const SUPPORTED_TYPES_FOR_MULTI_TEMPLATES_ADDING = [
    FIELD_TYPE_RECTANGLE,
    FIELD_TYPE_MULTI_LINE_TEXT,
    FIELD_TYPE_SAFE_ZONE,
    FIELD_TYPE_CIRCLE,
    FIELD_TYPE_LINE,
    FIELD_TYPE_BACKGROND_THEME_IMAGE,
    FIELD_TYPE_CUT_LINE
];

export default {
  name: 'ContentModule',
  components: {PreviewsDropdown},
  props: {
    switchToTab: {
      type: Function,
      default: () => function () {}
    } 
  },
  data() {
    return {
      selectedInstancesIds: [],
      fieldName: ''
    };
  },
  computed: {
    ...mapGetters('layout', [
      'selectedInstance',
      'selectedInstanceIndex',
      'selectedInstanceFieldsWithType',
      'currentPagePreviews',
    ]),
    hasSmartObject() {
      return this.selectedInstance.template.fields.some(field => field.type === 'Smart Object');
    },
    hasProductDimensionsField(){
      return this.selectedInstance.template.fields.some(field => field.type === 'Product Dimensions');
    },
  },
  methods: {
    addProductImages () {
      this.selectedInstance.canvas.fire('add:product-images', {
        instanceId: this.selectedInstance.id,
        isNeedAddProductDimensionsField: false,
      })
    },
    addProductImagePlaceholder () {
      this.selectedInstance.canvas.fire('add:product-image-placeholder', {
        instanceId: this.selectedInstance.id,
        isNeedAddProductDimensionsField: false,
      })
    },
    addProductDimensions () {
      this.selectedInstance.canvas.fire('add:product-dimensions', {
        instanceId: this.selectedInstance.id,
        isNeedAddProductDimensionsField: false,
      })
    },
    addAdditionalField (type) {
      const isMultiTemplateAddingSupported = SUPPORTED_TYPES_FOR_MULTI_TEMPLATES_ADDING.includes(type);

      this.selectedInstance.canvas.fire('add:additional-field', {
        instancesIds: isMultiTemplateAddingSupported ? this.selectedInstancesIds : [this.selectedInstance.id],
        type,
        options: {
          name: this.fieldName
        }
      })
    },
    showSmartObject () {
      this.selectedInstance.canvas.fire('show:smart-object-editor', {
        instanceId: this.selectedInstance.id,
        preview: this.selectedInstance
      })
      this.switchToTab(0)
    },
    addImage() {
      this.switchToTab(0);

      const totalExpectedNewObjects = this.selectedInstancesIds.length;
      const newObjects = [];

      const previews = window.previews.filter(preview => this.selectedInstancesIds.includes(preview.id));

      for (const preview of previews) {
        const existingElementIds = new Set();
        const existingIds = new Set();

        const currentObjects = this.selectedInstance.canvas.getObjects();
        currentObjects.forEach((obj, index) => {
          if (obj.element_id) existingElementIds.add(obj.element_id);
          if (obj.id) existingIds.add(obj.id);
        });

        preview.canvas.once('object:added', (e) => {
          setTimeout(() => {
            const updatedObjects = this.selectedInstance.canvas.getObjects();

            let newObject = null;

            updatedObjects.forEach((obj, index) => {
              if (obj.element_id && !existingElementIds.has(obj.element_id)) {
                newObject = obj;
              }
              else if (obj.id && !existingIds.has(obj.id)) {
                if (!newObject) newObject = obj;
              }
            });

            if (!newObject && updatedObjects.length > currentObjects.length) {
              newObject = updatedObjects[updatedObjects.length - 1];
            }

            newObjects.push({ instanceId: preview.id, objects: [newObject] });

            if (newObjects.length === totalExpectedNewObjects) {
              this.selectedInstance.canvas.setActiveObject(newObject);
              this.selectedInstance.canvas.renderAll();

              editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                action: 'selectBackground',
                targets: newObjects
              });
            }
          }, 50);
        });
      }
      
      this.addAdditionalField('Background Theme Image');
    }
  }
}
</script>

<style scoped lang="scss">
.btn-group-vertical {
  width: 100%;

  .btn {
    display: flex;
    align-items: center;

    .b-icon {
      margin-right: .5rem;
    }
  }
}
</style>
